import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { timeAgo } from '@/lib/format';
import { locale, t } from '@/lib/i18n';
import { type FlaggedBus, type LineCheck, useMoveToZone, type ZoneRef } from '@/lib/stats';
import { DEMAND_RAMP, SPEED_STOPS } from './map/heatmap-layers';
import { Button } from './ui/button';

const num = (n: number) => n.toLocaleString(locale);

/** Change against yesterday at the same time, e.g. "+12% vs yesterday". */
export function versus(today: number, before: number): string | undefined {
  if (!before) return undefined;
  const pct = Math.round(((today - before) / before) * 100);
  return t('{p} vs yesterday at this time', { p: `${pct > 0 ? '+' : ''}${pct}%` });
}

/**
 * Today by hour as bars, yesterday as a thin tick per hour for comparison. Hours still to come
 * are empty.
 */
export function HourBars({
  title,
  today,
  yesterday,
  hourNow,
}: {
  title?: string;
  today: number[];
  yesterday: number[];
  hourNow: number;
}) {
  const max = Math.max(1, ...today, ...yesterday);
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between text-xs">
        <span className="font-medium text-slate-700">{title}</span>
        <span className="flex items-center gap-3 text-slate-500">
          <span className="flex items-center gap-1">
            <span className="inline-block size-2.5 rounded-sm bg-brand-600/80" /> {t('Today')}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-3 bg-slate-500" /> {t('Yesterday')}
          </span>
        </span>
      </div>
      <div className="flex h-20 items-end gap-[2px] border-b border-slate-200">
        {today.map((v, h) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: one bar per hour
          <div key={h} className="group relative flex h-full flex-1 flex-col justify-end">
            {h <= hourNow && v > 0 && (
              <div
                className="rounded-t-[3px] bg-brand-600/80 group-hover:bg-brand-700"
                style={{ height: `${Math.max(3, (v / max) * 100)}%` }}
              />
            )}
            {yesterday[h]! > 0 && (
              <div
                className="absolute inset-x-0 h-0.5 bg-slate-500"
                style={{ bottom: `${(yesterday[h]! / max) * 100}%` }}
              />
            )}
            <div className="pointer-events-none absolute -top-9 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-slate-900 px-1.5 py-0.5 text-[11px] whitespace-nowrap text-white group-hover:block">
              {String(h).padStart(2, '0')}:00 · {t('Today')} {h <= hourNow ? num(v) : '—'} · {t('Yesterday')}{' '}
              {num(yesterday[h] ?? 0)}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-slate-400">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}

/** Most asked-about stations; clicking one shows it on the map. */
export function TopStations({
  items,
  onSelect,
}: {
  items: Array<{ _id: string; name: string; n: number }>;
  onSelect: (id: string) => void;
}) {
  if (!items.length) return <p className="text-sm text-slate-500">{t('No requests recorded yet.')}</p>;
  const max = items[0]!.n;
  return (
    <ol className="space-y-1.5 text-sm">
      {items.map((s, i) => (
        <li key={s._id}>
          <button type="button" onClick={() => onSelect(s._id)} className="group w-full text-left">
            <div className="flex items-baseline gap-2">
              <span className="w-4 text-xs text-slate-400">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate group-hover:underline">{s.name}</span>
              <span className="text-xs text-slate-600 tabular-nums">{num(s.n)}</span>
            </div>
            <div className="mt-0.5 ml-6 h-1 rounded-full bg-slate-100">
              <div className="h-1 rounded-full bg-brand-600/70" style={{ width: `${(s.n / max) * 100}%` }} />
            </div>
          </button>
        </li>
      ))}
    </ol>
  );
}

const pct = (x: number | null) => `${Math.round((x ?? 0) * 100)}%`;
const zoneName = (z: ZoneRef | null) => (z ? `${z.bus_sign} · ${z.name}` : '—');

/** One sentence for a line check: what the bus seems to do. */
function lineCheckText(check: LineCheck, zone: ZoneRef | null): string | null {
  const values = {
    own: zoneName(zone),
    other: zoneName(check.suggested_zone),
    p: pct(check.suggested_share),
    q: pct(check.zone_share),
  };
  switch (check.status) {
    case 'wrong_zone':
      return t(
        'Assigned to {own}, but in the last 30 minutes it drove {other} ({p} of positions; {q} on its own line).',
        values,
      );
    case 'unassigned':
      return t('Has no line, but in the last 30 minutes it drove {other} ({p} of positions).', values);
    case 'off_route':
      return t(
        'Off its route: only {q} of the last 30 minutes on {own}. A detour, or a line drawn differently from where buses drive.',
        values,
      );
    default:
      return null;
  }
}

/** Warning box for a bus whose driving does not match its zone, with the fix when there is one. */
export function LineCheckNotice({
  busId,
  check,
  zone,
  compact = false,
}: {
  busId: string;
  check: LineCheck | null | undefined;
  zone: ZoneRef | null;
  compact?: boolean;
}) {
  const move = useMoveToZone();
  if (!check || !['wrong_zone', 'off_route', 'unassigned'].includes(check.status)) return null;
  return (
    <div
      className={`rounded-md bg-amber-50 text-amber-900 ring-1 ring-amber-200 ${compact ? 'p-2 text-xs' : 'p-3 text-sm'}`}
    >
      <div className="flex gap-2">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <div className="min-w-0 space-y-2">
          <p>{lineCheckText(check, zone)}</p>
          {check.suggested_zone && (
            <Button
              size="sm"
              disabled={move.isPending}
              onClick={() => move.mutate({ bus: busId, zone: check.suggested_zone!._id })}
            >
              {t('Move to {line}', { line: check.suggested_zone.name })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const TABS = ['wrong_zone', 'unassigned', 'off_route'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = {
  wrong_zone: t('Wrong line'),
  unassigned: t('No line, drives one'),
  off_route: t('Off route'),
};

/**
 * Buses that do not seem to drive their zone, one row each, grouped by finding. Wrong zone first:
 * those have a one-click fix.
 */
export function LineAlerts({
  items,
  selected,
  onSelect,
}: {
  items: FlaggedBus[];
  /** The bus open on the map, highlighted in the table. */
  selected?: string | null;
  onSelect: (id: string) => void;
}) {
  const counts = Object.fromEntries(
    TABS.map((k) => [k, items.filter((b) => b.line_check.status === k).length]),
  );
  const [tab, setTab] = useState<Tab>(() => TABS.find((k) => counts[k]) ?? 'wrong_zone');
  const move = useMoveToZone();
  // Show the tab of the bus selected on the map.
  const selectedStatus = items.find((b) => b._id === selected)?.line_check.status as Tab | undefined;
  useEffect(() => {
    if (selectedStatus) setTab(selectedStatus);
  }, [selectedStatus]);
  if (!items.length)
    return <p className="px-4 py-3 text-sm text-slate-500">{t('All buses drive their line.')}</p>;
  const rows = items.filter((b) => b.line_check.status === tab);
  return (
    <div>
      <div className="flex gap-1 border-b border-slate-200 px-3">
        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === k
                ? 'border-brand-600 font-medium text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {TAB_LABEL[k]} <span className="text-xs text-slate-400">{counts[k]}</span>
          </button>
        ))}
      </div>
      <p className="px-4 pt-2 text-xs text-slate-500">
        {
          {
            off_route: t(
              'Little of the last 30 minutes on its own line, and no other line fits: a detour, a trip to the depot, or a line with a wrongly drawn direction.',
            ),
            wrong_zone: t(
              'In the last 30 minutes the bus drove another line than the one it is assigned to.',
            ),
            unassigned: t('The bus has no line, but in the last 30 minutes it drove one.'),
          }[tab]
        }
      </p>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">{t('Bus')}</th>
              <th className="px-4 py-2 font-medium">{t('Assigned line')}</th>
              <th className="px-4 py-2 font-medium">
                {tab === 'off_route' ? t('On its line') : t('Drives')}
              </th>
              <th className="px-4 py-2 font-medium">{t('Since')}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((b) => {
              const c = b.line_check;
              return (
                <tr
                  key={b._id}
                  className={
                    b._id === selected ? 'bg-amber-50 shadow-[inset_3px_0_0_#f5b700]' : 'hover:bg-slate-50'
                  }
                  aria-selected={b._id === selected}
                >
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => onSelect(b._id)}
                      className="font-medium hover:underline"
                    >
                      {b.number ? `${b.number} · ` : ''}
                      {b.name || b.arvento_id}
                    </button>
                    {b.is_visible === false && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                        {t('Hidden in the app')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{zoneName(b.zone)}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {tab === 'off_route'
                      ? pct(c.zone_share)
                      : `${zoneName(c.suggested_zone)} (${pct(c.suggested_share)})`}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{timeAgo(c.since)}</td>
                  <td className="px-4 py-2 text-right">
                    {c.suggested_zone && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={move.isPending}
                        onClick={() => move.mutate({ bus: b._id, zone: c.suggested_zone!._id })}
                      >
                        {t('Move to {line}', { line: c.suggested_zone.name })}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Legend for the heatmap shown on the map. */
export function HeatmapLegend({ kind }: { kind: 'bus' | 'user' }) {
  return (
    <div className="absolute bottom-2 left-2 z-10 rounded-md bg-white/95 px-2.5 py-2 text-[11px] text-slate-600 shadow ring-1 ring-slate-200">
      {kind === 'bus' ? (
        <>
          <div className="mb-1 font-medium text-slate-800">{t('Average bus speed')}</div>
          <div
            className="h-2 w-40 rounded-full"
            style={{ background: `linear-gradient(to right, ${SPEED_STOPS.map((s) => s[1]).join(', ')})` }}
          />
          <div className="mt-0.5 flex w-40 justify-between">
            <span>&lt; {SPEED_STOPS[0]![0]} km/h</span>
            <span>&gt; {SPEED_STOPS.at(-1)![0]} km/h</span>
          </div>
          <div className="mt-1 text-slate-500">
            {t('Bigger, stronger dots: buses spend more time there.')}
          </div>
        </>
      ) : (
        <>
          <div className="mb-1 font-medium text-slate-800">{t('Where passengers use the app')}</div>
          <div
            className="h-2 w-40 rounded-full"
            style={{ background: `linear-gradient(to right, ${DEMAND_RAMP.slice(1).join(', ')})` }}
          />
          <div className="mt-0.5 flex w-40 justify-between">
            <span>{t('few')}</span>
            <span>{t('many')}</span>
          </div>
        </>
      )}
    </div>
  );
}
